// @flow
import * as React from "react";

import { Input, Modal } from "antd";
import type { InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import AuthService from "services/auth";

import { getPagesForRole } from "./pages";

import "./index.scss";

type ResultType = "offer" | "affiliate" | "advertiser" | "page";

type SearchResult = {
  type: ResultType,
  id: number | string,
  label: string,
  url: string,
};

type State = {
  offers: { items: Map<number, string> },
  affiliates: { items: Map<number, string> },
  advertisers: { items: Map<number, string> },
};

const RESULT_LIMIT = 15;

const BADGE_LABELS: { [ResultType]: string } = {
  offer: "Offer",
  affiliate: "Affiliate",
  advertiser: "Advertiser",
  page: "Page",
};

function isMacPlatform(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
}

function matchesQuery(normalizedQuery: string, ...parts: Array<string | number>): boolean {
  return parts.some((part) => String(part).toLowerCase().includes(normalizedQuery));
}

function useSearchResults(query: string): SearchResult[] {
  const claims = AuthService.getClaims();
  const offers = useSelector((state: State) => state.offers.items);
  const affiliates = useSelector((state: State) => state.affiliates.items);
  const advertisers = useSelector((state: State) => state.advertisers.items);

  return React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const results: SearchResult[] = [];

    if (!normalizedQuery) {
      return getPagesForRole(claims.rol)
        .slice(0, RESULT_LIMIT)
        .map(({ label, path }) => ({
          type: "page",
          id: path,
          label,
          url: path,
        }));
    }

    getPagesForRole(claims.rol).forEach(({ label, path }) => {
      if (matchesQuery(normalizedQuery, label, path)) {
        results.push({
          type: "page",
          id: path,
          label,
          url: path,
        });
      }
    });

    offers.forEach((name, id) => {
      if (matchesQuery(normalizedQuery, id, name)) {
        results.push({
          type: "offer",
          id,
          label: name,
          url: `/offers/${id}`,
        });
      }
    });

    affiliates.forEach((name, id) => {
      if (matchesQuery(normalizedQuery, id, name)) {
        results.push({
          type: "affiliate",
          id,
          label: name,
          url: `/affiliates/${id}`,
        });
      }
    });

    advertisers.forEach((name, id) => {
      if (matchesQuery(normalizedQuery, id, name)) {
        results.push({
          type: "advertiser",
          id,
          label: name,
          url: `/advertisers/${id}`,
        });
      }
    });

    return results.slice(0, RESULT_LIMIT);
  }, [advertisers, affiliates, claims.rol, offers, query]);
}

function SearchBar(): React.Node {
  const [open, setOpen] = React.useState<boolean>(false);
  const [query, setQuery] = React.useState<string>("");
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const inputRef = React.useRef<?InputRef>(null);

  const shortcutLabel = isMacPlatform() ? "⌘K" : "Ctrl+K";
  const results = useSearchResults(query);

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
  };

  const handleSelect = (result: SearchResult) => {
    closeModal();

    if (result.type === "page") {
      window.location.assign(result.url);
      return;
    }

    window.open(result.url, "_blank");
  };

  const handleInputKeyDown = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(results.length - 1, 0)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter" && results[activeIndex]) {
      event.preventDefault();
      handleSelect(results[activeIndex]);
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  React.useEffect(() => {
    if (open) {
      window.setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [open]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  return (
    <>
      <li className="nav-item SearchBar d-flex align-items-center">
        <button
          type="button"
          className="Header-control search-bar__trigger"
          aria-label="Search or jump to"
          onClick={openModal}>
          <SearchOutlined className="search-bar__trigger-icon" />
          <span className="search-bar__trigger-text">Search or jump to...</span>
          <kbd className="search-bar__shortcut">{shortcutLabel}</kbd>
        </button>
      </li>

      <Modal
        open={open}
        footer={null}
        centered
        width={560}
        className="search-bar__modal"
        destroyOnClose
        onCancel={closeModal}>
        <div className="search-bar__input-row">
          <SearchOutlined className="search-bar__input-icon" />
          <Input
            ref={inputRef}
            variant="borderless"
            className="search-bar__input"
            placeholder="Jump to offer, affiliate, advertiser, or page..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <kbd className="search-bar__shortcut">ESC</kbd>
        </div>

        <div className="search-bar__results">
          {results.length === 0 ? (
            <div className="search-bar__empty">No results found</div>
          ) : (
            results.map((result, index) => (
              <button
                key={`${result.type}-${result.id}`}
                type="button"
                className={`search-bar__result${index === activeIndex ? " search-bar__result--active" : ""}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(result)}>
                <span className="search-bar__badge">{BADGE_LABELS[result.type]}</span>
                <span className="search-bar__label">{result.label}</span>
                {result.type !== "page" && <span className="search-bar__id">{result.id}</span>}
              </button>
            ))
          )}
        </div>
      </Modal>
    </>
  );
}

export default SearchBar;
